import React, { useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Radio,
  Dialog,
  DialogContent,
  DialogTitle,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { ImageData } from "../../../../models/Image/Image";

// Extended interface for internal use that allows both string and number IDs
interface ExtendedImageData {
  id: string | number;
  img: string;
  title?: string;
  is_default?: boolean;
}

interface MediaAssetsUploadProps {
  existingImageList?: ImageData[];
  newImageList?: File[];
  deleteImage?: (image: ImageData) => void;
  removeImage?: (image: {
    id: string | number;
    title?: string;
    img: string;
  }) => void;
  addImagesToBeUploaded?: (files: File[]) => void;
  isViewOnly?: boolean;
  multiple?: boolean;
  uploadText?: string;
  fileInfo?: string;
  uploadIcon?: React.ReactNode;
  onSetDefaultImage?: (imageId: number) => void;
  maxWidth?: string;
  cardHeight?: string;
  cardWidth?: string;
  hideDeleteForExistingImages?: boolean;
}

const MediaAssetsUpload: React.FC<MediaAssetsUploadProps> = ({
  existingImageList = [],
  newImageList = [],
  deleteImage,
  removeImage,
  addImagesToBeUploaded,
  isViewOnly = false,
  multiple = true,
  uploadText = "Upload Image",
  fileInfo = "PNG, JPG up to 10MB each",
  uploadIcon,
  onSetDefaultImage,
  maxWidth = "100%",
  cardHeight = "180px",
  cardWidth = "160px",
  hideDeleteForExistingImages = false,
}) => {
  const theme = useTheme();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleViewImage = (img: string) => {
    setSelectedImage(img);
  };

  const handleClosePopup = () => {
    setSelectedImage(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (addImagesToBeUploaded) {
      addImagesToBeUploaded(files);
    }
    // Reset input value to allow selecting the same file again
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const handleDeleteImage = (image: ExtendedImageData) => {
    if (deleteImage && typeof image.id === "number") {
      // Check if this is the default image being deleted
      const isDeletingDefault = image.is_default;

      deleteImage({
        id: image.id,
        img: image.img,
        title: image.title,
      });

      // If we're deleting the default image and there are other existing images,
      // automatically set the first available one as default
      if (isDeletingDefault && onSetDefaultImage) {
        const remainingExistingImages = existingImageList.filter(
          (img) => img.id !== image.id && typeof img.id === "number"
        );

        if (remainingExistingImages.length > 0) {
          // Set the first remaining image as default
          const newDefaultImage = remainingExistingImages[0];
          onSetDefaultImage(newDefaultImage.id as number);
        }
      }
    }
  };

  const handleRemoveImage = (image: ExtendedImageData) => {
    if (removeImage) {
      // Check if this is the default image being removed
      const isRemovingDefault = image.is_default;

      removeImage(image);

      // If we're removing the default image and there are other existing images,
      // automatically set the first available one as default
      if (isRemovingDefault && onSetDefaultImage) {
        const remainingExistingImages = existingImageList.filter(
          (img) => img.id !== image.id && typeof img.id === "number"
        );

        if (remainingExistingImages.length > 0) {
          // Set the first remaining image as default
          const newDefaultImage = remainingExistingImages[0];
          onSetDefaultImage(newDefaultImage.id as number);
        }
      }
    }
  };

  // Combine existing and new images
  const allImages: ExtendedImageData[] = [
    ...existingImageList.map((img, index) => ({
      id: img.id !== undefined ? img.id : `existing-${index}`,
      img: img.img,
      title: img.title,
      is_default: (img as any).is_default || false,
    })),
    ...newImageList.map((file, index) => ({
      id: `file-${index}`,
      title: file.name,
      img: URL.createObjectURL(file),
      is_default: false,
    })),
  ];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth,
        overflowX: "scroll",
        padding: "0.3rem",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        "-ms-overflow-style": "none", // IE and Edge
        scrollbarWidth: "none", // Firefox
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          width: "100%",
          minHeight: cardHeight,
          alignItems: "flex-start",
        }}
      >
        {/* Upload Card - Always present */}
        <Card
          sx={{
            height: cardHeight,
            width: cardWidth,
            minWidth: cardWidth,
            flexShrink: 0,
            border: `2px dashed ${
              theme.palette.customColors?.greyBackground || "#e0e0e0"
            }`,
            backgroundColor: "transparent",
            cursor: isViewOnly ? "default" : "pointer",
            transition: "all 0.3s ease",
            "&:hover": {
              borderColor: isViewOnly ? undefined : theme.palette.primary.main,
              backgroundColor: isViewOnly
                ? undefined
                : theme.palette.action.hover,
            },
          }}
          onClick={isViewOnly ? undefined : handleUploadClick}
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              textAlign: "center",
              p: 2,
            }}
          >
            <input
              ref={inputRef}
              type="file"
              multiple={multiple}
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              disabled={isViewOnly}
            />

            {uploadIcon || (
              <AddIcon
                sx={{
                  fontSize: 40,
                  color: theme.palette.text.secondary,
                  mb: 1,
                }}
              />
            )}

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.5, fontWeight: 500 }}
            >
              {uploadText}
            </Typography>

            {fileInfo && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem" }}
              >
                {fileInfo}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Image Cards */}
        {allImages.map((image) => (
          <Card
            key={image.id}
            sx={{
              height: cardHeight,
              width: cardWidth,
              minWidth: cardWidth,
              flexShrink: 0,
              position: "relative",
              overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: theme.shadows[8],
                "& .overlay": {
                  opacity: 1,
                },
              },
            }}
          >
            {/* Image */}
            <Box
              sx={{
                height: "100%",
                width: "100%",
                position: "relative",
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 1,
                }}
              >
                <img
                  src={image.img}
                  alt="ima-ge"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
              {/* Radio button - Only visible for existing images with database IDs when onSetDefaultImage is provided */}
              {onSetDefaultImage && typeof image.id === "number" && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    zIndex: 10,
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Tooltip title="Click to set as default image">
                    <Radio
                      checked={image.is_default}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetDefaultImage(image.id as number);
                      }}
                      sx={{
                        color: image.is_default ? "purple" : "grey",
                        "&.Mui-checked": {
                          color: "purple",
                        },
                        "& .MuiRadio-icon": {
                          fontSize: 20,
                        },
                        // Remove padding changes that cause position shifts
                        padding: 0,
                        // Ensure consistent size
                        width: 24,
                        height: 24,
                        // Remove background styling that affects layout
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "rgba(128, 0, 128, 0.1)",
                        },
                      }}
                    />
                  </Tooltip>
                </Box>
              )}

              {/* Overlay with actions - Only visible on hover */}
              <Box
                className="overlay"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 1,
                }}
              >
                {/* Center actions container */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Tooltip title="View Image">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewImage(image.img);
                      }}
                      sx={{
                        color: "white",
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.3)",
                        },
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {!isViewOnly &&
                    !(
                      hideDeleteForExistingImages &&
                      typeof image.id === "number"
                    ) && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Check if this is a new file (string ID starting with "file-")
                        if (
                          typeof image.id === "string" &&
                          image.id.startsWith("file-")
                        ) {
                          // New file - always use removeImage
                          handleRemoveImage(image);
                        } else {
                          // Existing image - determine which callback to use based on the image type
                          // For size chart, we'll use removeImage. For product images, we'll use deleteImage
                          if (image.title === "Size Chart") {
                            // Size chart image - use removeImage
                            handleRemoveImage(image);
                          } else {
                            // Product image - use deleteImage if available, otherwise removeImage
                            if (deleteImage) {
                              handleDeleteImage(image);
                            } else {
                              handleRemoveImage(image);
                            }
                          }
                        }
                      }}
                      sx={{
                        color: "white",
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.3)",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Box>
          </Card>
        ))}
      </Box>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={handleClosePopup}
        maxWidth="md"
        fullWidth
        sx={{
          zIndex: 3000,
        }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Image Preview</Typography>
            <IconButton onClick={handleClosePopup}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <img
                src={selectedImage}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MediaAssetsUpload;
